import {
    isSupported
} from '@mapbox/mapbox-gl-supported';

import {
    Map as MapLibre,
    addProtocol,
    LngLatBounds,
    NavigationControl,
    FullscreenControl,
    ScaleControl,
    Marker,
    Popup,
    getWorkerCount,
    setWorkerCount,
    setWorkerUrl
} from 'maplibre-gl';

import {
    isMapboxURL,
    transformMapboxUrl
} from 'maplibregl-mapbox-request-transformer';

import {Protocol as PMTilesProtocol} from 'pmtiles';

import {pushError} from '@js/actions/errorManagement';
import {closeOpenMenus, hasOpenMenu} from '@js/components/themes/actions/clickOutside';

import I18n from '@js/modules/translations';
import * as Utils from '@js/modules/utils';

import {MAP_MODULES, STYLE_SETTER_OPTIONS, COORDINATES_PRECISION} from '@js/modules/maps/mapConstants';
import {whenMapUsable, deferDemLayers, revealLayers, loadMarkerImages} from '@js/modules/maps/mapLoadHelpers';
import {makeMarkerKeyboardDraggable} from '@js/modules/maps/modules/markerKeyboardDrag';
import {SLOPE_BANDS} from '@js/modules/maps/slopeGrade';
import MapTileService from '@js/modules/maps/mapTileService';
import Terrain3DControl from '@js/modules/maps/3dButton';

import StorageService from '@js/modules/storage';
import AnalyticsService from '@js/modules/analyticsService';
import WebPerf from '@js/modules/webPerf';

import Notification from '@js/components/layouts/notification';

import markerIcon from '@/images/maps/marker-icon.png';
import markerArrowIcon from '@/images/maps/marker-arrow.png';
import markerStopIcon from '@/images/maps/marker-stop-play.png';
import markerRunningIcon from '@/images/maps/marker-running-primary.png';
import markerHikingIcon from '@/images/maps/marker-hiking-primary.png';
import markerBikeIcon from '@/images/maps/marker-bike-primary.png';
import markerWalkIcon from '@/images/maps/marker-walk-primary.png';
import markerBlankIcon from '@/images/maps/ride_blank_marker.png';


if (window.maplibreWorkerUrl) {
    // The worker is served from the cross-origin asset host (assets.map-planner.* in prod, the
    // rsbuild dev server on :8080 in dev). Browsers forbid a cross-origin *top-level* worker script
    // even for module workers — and maplibre v6 constructs its worker with { type: 'module' } — so
    // `new Worker(cdnUrl)` throws SecurityError and the map silently never starts. A module worker
    // CAN however `import` a cross-origin URL when the server sends CORS (the asset host sends
    // Access-Control-Allow-Origin: *). So point maplibre at a same-origin blob module worker that
    // just imports the real worker. NB: importScripts() is unavailable in module workers, hence the
    // static import. Runs synchronously here, before the first map is created.
    // The real worker runs cross-origin (asset host): any exception it throws is muted to a generic
    // "Script error." by the time it reaches the main thread, so Sentry only ever sees noise. The
    // imported module is CORS-clean (asset host sends Access-Control-Allow-Origin: *), so INSIDE the
    // worker the error still carries its real message/stack. Install global handlers that relay that
    // detail to the main thread via postMessage, where workerErrorReporting.js turns it into a proper
    // Sentry event. The payload intentionally omits an `origin` field so maplibre's Actor ignores it
    // (Actor.receive drops any message whose origin !== location.origin). The static import stays
    // hoisted so maplibre installs its own message listeners before any message is processed.
    // Browsers without module worker support (older Firefox/Safari) either throw on
    // { type: 'module' } — maplibre then falls back to a classic worker — or ignore the option
    // altogether. Either way the shim runs as a classic script, where an `import` declaration is a
    // syntax error and the map never starts. Detect it and use importScripts() instead: the worker
    // bundle rsbuild emits is a classic script, so it loads that way too.
    const supportsModuleWorker = () => {
        let supported = false;
        const probeUrl = URL.createObjectURL(new Blob([''], {type: 'application/javascript'}));

        try {
            new Worker(probeUrl, {get type() {
                supported = true;

                return 'module';
            }}).terminate();
        } catch {
            supported = false;
        } finally {
            URL.revokeObjectURL(probeUrl);
        }

        return supported;
    };

    const workerLoad = supportsModuleWorker()
        ? `import ${JSON.stringify(window.maplibreWorkerUrl)};`
        : `importScripts(${JSON.stringify(window.maplibreWorkerUrl)});`;

    const workerShim = `
${workerLoad}
const __mpRelay = (detail) => {
  try { self.postMessage({__mpWorkerError: detail}); return true; } catch (_) { return false; }
};
self.addEventListener('error', (e) => {
  const relayed = __mpRelay({
    message: e.message, filename: e.filename, lineno: e.lineno, colno: e.colno,
    stack: e.error && e.error.stack
  });
  // Once relayed with full detail, suppress the muted duplicate that would reach the main thread.
  if (relayed) { e.preventDefault(); }
});
self.addEventListener('unhandledrejection', (e) => {
  const reason = e.reason;
  __mpRelay({message: (reason && reason.message) || String(reason), stack: reason && reason.stack, unhandledRejection: true});
});
`;
    setWorkerUrl(URL.createObjectURL(new Blob([workerShim], {type: 'application/javascript'})));
}

// MapLibre spawns a single tile-parsing worker by default (except on Safari),
// which serializes vector-tile decoding on initial load. Use more workers to
// parallelize it. Must run before the first map is created; only override the
// conservative default of 1 so Safari's own tuned value is left untouched.
if (typeof navigator !== 'undefined' && getWorkerCount() === 1) {
    setWorkerCount(Math.min(navigator.hardwareConcurrency || 4, 8));
}


export default class MapInterface {
    static type = {
        COMPUTE_DISTANCE: 1,
        RIDE: 2,
        SELECTED: 3,
        PLAN: 4,
        IMPORT: 5,
        ALL: 6
    };

    static mode = {
        EXPLORE: 1,
        SHOW: 2,
        CHOROPLETH: 3,
        ASSOCIATION: 4
    };

    static routingService = {
        MAP_PLANNER: 'map_planner',
        MAPBOX: 'mapbox'
    };

    static mapMinZoom = 2;

    // Map center and bbox
    static europeMaxBounds = [
        [-34.49296, 29.735139], // Southwest coordinates (lng, lat)
        [46.75348, 81.47299] // Northeast coordinates (lng, lat)
    ];

    // static europeMinZoom = 5;

    static northAmericaMaxBounds = [
        [-168.697139, 23.857636], // Southwest coordinates (lng, lat)
        [-44.368171, 74.329834] // Northeast coordinates (lng, lat)
    ];

    // static northAmericaMinZoom = 5;

    static userZoom = 11;

    // No IP location: the map falls back to the country centre, shown at region scale.
    static countryZoom = 8;

    static COORDINATES_PRECISION = COORDINATES_PRECISION;

    static DEG_TO_RAD = 0.0174532925199433;

    static EARTH_RADIUS = 6371.0088;

    static PointInfo = function (latLng, keyPoint, pointData) {
        pointData ||= {};

        this.keyPoint = keyPoint;
        this.latLng = latLng;

        this.computed = pointData.computed;

        this.deltaDistance = pointData.deltaDistance || 0;
        this.deltaTime = pointData.deltaTime || 0;
        this.deltaPause = pointData.deltaPause || 0;
        this.datetime = pointData.datetime;
        this.elevation = pointData.elevation;
        this.speed = pointData.speed;
        this.heartRate = pointData.heartRate;
        this.surface = pointData.surface;

        // Freehand segment kept as drawn because no nearby road matched (rendered distinctly).
        this.offRoad = pointData.offRoad;
    };

    static MarkerInfo = function (latLng, type, data) {
        this.latLng = latLng;
        this.type = type;
        this.data = data;
    };

    static MarkerPoint = function (marker, markerId, popup) {
        this.marker = marker;
        this.markerId = markerId;
        this.popup = popup;
    };

    static DirectionArrow = function (keyPoint, linePointIndex) {
        this.keyPoint = keyPoint;
        this.linePointIndex = linePointIndex;
    };

    static SURFACE_GROUPS = {
        paved: {
            surfaces: ['asphalt', 'concrete', 'paved', 'paving_stones', 'cobblestone', 'sett'],
            color: '#616161'
        },
        compacted: {
            surfaces: ['compacted', 'fine_gravel'],
            color: '#FF9800'
        },
        gravel: {
            surfaces: ['gravel'],
            color: '#FFC107'
        },
        dirt: {
            surfaces: ['dirt', 'earth', 'ground', 'mud', 'sand'],
            color: '#8D6E63'
        },
        natural: {
            surfaces: ['grass', 'grass_paver', 'wood'],
            color: '#66BB6A'
        },
        unknown: {
            surfaces: [],
            color: '#9E9E9E'
        }
    };

    static ROAD_CLASS_TO_SURFACE = {
        motorway: 'asphalt',
        trunk: 'asphalt',
        primary: 'asphalt',
        secondary: 'asphalt',
        tertiary: 'asphalt',
        residential: 'asphalt',
        unclassified: 'compacted',
        service: 'asphalt',
        track: 'gravel',
        path: 'dirt',
        footway: 'dirt',
        cycleway: 'asphalt',
        bridleway: 'dirt',
        steps: 'paved',
        living_street: 'asphalt',
        pedestrian: 'paved'
    };

    static surfaceFromRoadClass(roadClass) {
        return MapInterface.ROAD_CLASS_TO_SURFACE[roadClass] || null;
    }

    static getSurfaceGroup(surfaceType) {
        if (!surfaceType) return 'unknown';
        for (const [group, config] of Object.entries(MapInterface.SURFACE_GROUPS)) {
            if (config.surfaces.includes(surfaceType)) return group;
        }
        return 'unknown';
    }

    _mapAvailableModules = [];

    _model = {
        // container for map
        mapId: undefined,
        $mapId: undefined,

        // Map data
        map: undefined,
        mapStyle: undefined,
        mapStyleName: undefined,
        mapDemSource: undefined,
        mapSources: [],
        mapLayers: [],
        openPopups: [],
        mapCustomMarkers: [],
        mapTrackLineColor: '#327d1c',
        mapTrackOutlineColor: '#5de372',
        defaultFlyDuration: 1800,
        isLongTouching: false,
        // Name of the source track line
        mapTrackLineName: 'track-line',
        mapSecondaryLineProps: {
            opacity: 0.5
        },

        isMapLoaded: false,
        // Set by destroy(): a late network answer must not draw on (or into the state of) a dead map.
        isDestroyed: false,
        isStyleLoaded: false,
        isSourceLoaded: false,
        mapLoadedCallbacks: [],

        isDoubleClick: false,

        clusterMode: false,

        // Elevation map
        elevationMap: undefined,

        // Add map usage limitations
        hasMapLimitations: true,
        mapMaxRasterTilesCount: undefined,
        mapMaxVectorTilesCount: undefined,
        mapMaxRouteComputationCount: undefined,
        mapMaxIsochrone: undefined,
        mapMaxRoundTrip: undefined,
        mapMaxDistance: undefined,

        // params for creation
        initialLocation: undefined,
        initialZoom: undefined,

        // Default values if no points
        defaultLatitude: 47,
        defaultLongitude: 2,

        // Default zoom to display the ride or when displaying the map
        defaultZoom: undefined,
        rideZoom: 11,
        searchZoom: 14,

        // params for creation
        initialLat: undefined,
        initialLong: undefined,

        // bounds provide by user
        mapBounds: null,
        // Reset bounds for a new request
        resetBounds: false,
        // // Initial zoom after fit bounds
        // initZoom: true,
        // Add to bounds
        includeBounds: null,
        // Detect if zoom comes from fit founds
        isAutoZoom: false,
        // Min zoom after fit bounds
        minZoom: undefined,

        // save map bounds when dragging map for next display
        reusePosition: false,

        // Search marker
        searchMarker: undefined,

        // Activate gesture functionalities
        gestureEnabled: false,

        // km or mi
        distanceUnits: window.unit === 'm' ? 'km' : window.unit,

        // Minimum zoom to display tracks lines
        zoomPolyline: window.settings.min_zoom_polyline,

        // Containers for map buttons
        $styleButtonContainers: undefined,

        // Storage for bounds, for each user if possible
        storage: '',

        // Save previous user zoom
        previousZoom: undefined,

        // snapToRoads
        snapToRoads: true,
        // Freehand draw mode (draw the trace with mouse/finger, then match it to the roads)
        freehandDraw: false,
        isComputingRoad: false,
        previousRoadComputedPoints: 0,
        routingService: null,

        // Routing
        routingRouteType: 'hike',
        // Set when computing the first point (changed to Mapbox if unavailable)
        currentRoutingService: undefined,
        computingRoadCallback: undefined,

        // Object of {distance: , {lat:, lng:}} enumerated by distance traveled (used by highCharts for hover values)
        // Distance are in km or miles
        pointsByDistanceTraveled: {},

        // Filter requests when fetching
        filter: {},

        // Product
        showProductModal: undefined
    };

    alertPopup = undefined;

    constructor(modules, params = {}) {
        this._mapAvailableModules = modules;

        this._model.hasMapLimitations = params.hasMapLimitations;
        this._model.mapMaxRasterTilesCount = params.mapMaxRasterTilesCount;
        this._model.mapMaxVectorTilesCount = params.mapMaxVectorTilesCount;

        this._model.mapMaxDistance = params.mapMaxDistance;
        this._model.mapMaxRouteComputationCount = params.mapMaxRouteComputationCount;
        this._model.mapMaxIsochrone = params.mapMaxIsochrone;
        this._model.mapMaxRoundTrip = params.mapMaxRoundTrip;

        this._model.mapCustomMarkers = params.mapMarkers || [];

        this._model.clusterMode = params.clusterMode;

        this._model.initialLocation = params.initialLocation;
        this._model.initialLat = params.initialLat;
        this._model.initialLong = params.initialLong;

        this._model.initialZoom = params.initialZoom;
        this._model.defaultZoom = params.defaultZoom;
        this._model.minZoom = params.minZoom;

        if (Utils.isPresent(params.reusePosition)) {
            this._model.reusePosition = params.reusePosition;
        }

        if (Utils.isPresent(params.resetBounds)) {
            this._model.resetBounds = params.resetBounds;
        }

        if (Utils.isPresent(params.includeBounds)) {
            this._model.includeBounds = params.includeBounds;
        }

        if (Utils.isPresent(params.snapToRoads)) {
            this._model.snapToRoads = params.snapToRoads;
        }

        if (params.distanceUnits) {
            this._model.distanceUnits = params.distanceUnits;
        }

        this._model.mapId = params.mapId;
        this._model.$mapId = document.getElementById(params.mapId);

        if (Utils.isPresent(params.filter)) {
            this._model.filter = params.filter;
        }

        this._model.showProductModal = params.showProductModal;

        if (Utils.isEmpty(this._model.mapId)) {
            throw new Error('Set a valid map id: ' + this._model.mapId);
        }

        if (!this._model.$mapId) {
            throw new Error('Div Map container not found, cannot display map: #' + this._model.mapId);
        }

        if (this.hasModule('GEOLOCATION') && this.plugGeolocator) {
            this.plugGeolocator({
                mapId: params.mapId,
                isTracking: params.isGeoTracking,
                showRotateMap: params.showRotateMap
            });
        }

        // Create map
        const mapPosition = this._getCurrentMapPosition();
        const mapCreatedAt = performance.now();
        this._createMap({
            mapId: this._model.mapId + '-map',
            boundingBox: mapPosition.boundingBox,
            center: mapPosition.center,
            zoom: mapPosition.zoom,
            hideMapScale: params.hideMapScale,
            gestureEnabled: this._model.gestureEnabled,
            mapLimitations: this._model.hasMapLimitations ? {
                mapMaxRasterTilesCount: this._model.mapMaxRasterTilesCount,
                mapMaxVectorTilesCount: this._model.mapMaxVectorTilesCount
            } : false,
            geolocator: this.hasModule('GEOLOCATION') && this._geolocationModel ? this._geolocationModel.geolocator : undefined
        });

        if (!this._model.map) {
            return;
        }

        // A map created without a size (collapsed panel, unopened tab) renders only once revealed. Read
        // the container: MapLibre gives an unsized one a default 400x300 canvas.
        const createdContainer = this._model.map.getContainer?.();
        const createdWithSize = Boolean(createdContainer?.clientWidth && createdContainer?.clientHeight);

        // Change zoom speed for mouse wheel and trackpad (default is 1/450)
        this._model.map.scrollZoom?.setWheelZoomRate(1 / 250);

        MapTileService.showProductModal = this._model.showProductModal;

        this._model.mapRuntimeErrors = [];
        this._model.map.on('error', (event) => {
            const error = event && event.error ? event.error : event;
            const message = (error && error.message) || String(error);
            this._model.mapRuntimeErrors.push(message);
            if (this._model.mapRuntimeErrors.length > 5) {
                this._model.mapRuntimeErrors.shift();
            }
            console.error('[maplibre]', error);
        });

        // Track whether the tab was ever backgrounded during the load window: MapLibre throttles
        // (and can fully pause) rendering in hidden tabs, so a "never rendered" trip is expected
        // and benign there — the dominant false positive of this watchdog.
        this._model.tabHiddenDuringLoad = document.hidden;
        const onVisibilityChange = () => {
            if (document.hidden) {
                this._model.tabHiddenDuringLoad = true;
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        this._model.clearLoadWatchers = () => {
            clearTimeout(this._model.loadWatchdog);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };

        // Network / abort failures are environmental (offline, flaky mobile connection, ad-blockers,
        // or the user navigating away mid-load) — not code bugs, so they are not worth reporting.
        const isBenignError = (message) => /Failed to fetch|NetworkError|Load failed|AbortError|aborted|ERR_/i.test(message || '');

        const fireLoadWatchdog = () => {
            // Torn down, or the map actually loaded after all (load-event race) — nothing to report.
            if (!this._model.map || this._model.mapFiredLoad || this._model.map.loaded()) {
                return;
            }

            // Hidden tab (now or earlier) or offline: benign render throttle / no connectivity — skip.
            if (document.hidden || this._model.tabHiddenDuringLoad || !navigator.onLine) {
                return;
            }

            const styleLoaded = this._model.map.isStyleLoaded();
            const runtimeErrors = (this._model.mapRuntimeErrors || []).filter((message) => !isBenignError(message));

            // One extra grace period before concluding the map is really stuck.
            if (!this._model.watchdogGraceUsed) {
                this._model.watchdogGraceUsed = true;
                this._model.loadWatchdog = setTimeout(fireLoadWatchdog, 10000);
                return;
            }

            // Style never loaded and no concrete error left after filtering = almost always slow or
            // aborted network. Unactionable, so degrade silently instead of reporting.
            if (!runtimeErrors.length && !styleLoaded) {
                return;
            }

            // No concrete error and a 0-sized drawing buffer: the container has no layout box
            // (collapsed panel, display:none ancestor, a tab never opened). MapLibre does not paint
            // into a zero-sized canvas and fires "load" only after a first render, so "never
            // rendered" is the expected outcome there, not a failure. This is the remaining source
            // of "slow-render-stuck" on a visible, online tab.
            const canvas = this._model.map.getCanvas?.();
            if (!runtimeErrors.length && (!canvas || !canvas.width || !canvas.height)) {
                return;
            }

            // Only real failures remain: a concrete non-network init error, or a genuinely stuck
            // first render on a visible/online tab (e.g. dead worker, lost WebGL context).
            // Counted in the admin availability rate, not sent to Sentry: nothing there is actionable.
            AnalyticsService.trackMapRender('stuck');
        };
        this._model.loadWatchdog = setTimeout(fireLoadWatchdog, 15000);

        // isMapLoaded turns true at style.load: the watchdog needs the first render itself.
        this._model.map.once('load', () => {
            this._model.mapFiredLoad = true;
            this._model.clearLoadWatchers();
            // A hidden tab or an unsized map does not render: its load time says nothing about the map.
            if (!this._model.tabHiddenDuringLoad && createdWithSize) {
                WebPerf.recordMapReady(mapCreatedAt);
            }
        });

        whenMapUsable(this._model.map, () => {
            this._initializeSourcesAndLayers(() => {
                if (this.hasModule('GEOLOCATION') && this.plugGeolocatorButtons) {
                    this.plugGeolocatorButtons();
                }

                this._mapLoaded({
                    mapLoaded: true
                });
            }, params);
        });

        const alertPopupTriggers = ['pointerdown', 'keydown', 'dragenter'];
        let alertPopupRequested = false;
        const loadAlertPopup = () => {
            // Each listener holds this map: drop them all once one fired, or a destroyed map stays alive.
            alertPopupTriggers.forEach((eventName) => {
                document.removeEventListener(eventName, loadAlertPopup, {capture: true});
            });

            if (alertPopupRequested) {
                return;
            }
            alertPopupRequested = true;

            import(/* webpackChunkName: "theme-swal" */ '@js/modules/swal')
                .then((sweetalert2) => {
                    this.alertPopup = sweetalert2.default;
                })
                .catch((error) => {
                    alertPopupRequested = false;

                    pushError(error, {chunk: 'theme-swal'});
                });
        };

        // Some call sites use alertPopup unguarded: never later than the former 2.6 s after DOM ready.
        alertPopupTriggers.forEach((eventName) => {
            document.addEventListener(eventName, loadAlertPopup, {capture: true, once: true, passive: true});
        });
        Utils.onPageReady(loadAlertPopup, 2600);
    }

    onMapLoaded = (callbackFunction) => {
        if (this._model.isMapLoaded) {
            callbackFunction();
        } else {
            this._model.mapLoadedCallbacks.push(callbackFunction);
        }
    };

    hasModule = (moduleName) => {
        if (!this._mapAvailableModules || !this._mapAvailableModules.length) {
            return false;
        }

        if (!Object.values(MAP_MODULES)
            .includes(moduleName)) {
            throw `Unknown module to load: ${moduleName}`;
        }

        return this._mapAvailableModules.includes(moduleName);
    };

    setSnapToRoad = (value) => {
        this._model.snapToRoads = value;
    };

    setRouteType = (routeType) => {
        this._model.routingRouteType = routeType;
    };

    // Prefer paths / avoid car roads (routing only, no map layer). Read by computeRoadMap to pick the
    // "*_safe" GraphHopper profile. Default is driven per-activity by the caller (on for hiking).
    setPreferPathsMode = (enabled) => {
        this._model.preferPathsEnabled = enabled;
    };

    setMaxDistance = (maxDistanceMeters) => {
        if (maxDistanceMeters === null) {
            return;
        }

        this._model.mapMaxDistance = maxDistanceMeters;
    };

    onComputingRoad = (callback) => {
        this._model.computingRoadCallback = callback;
    };

    /* options: */
    /* - zoom: max zoom on fit bounds */
    /* - padding: padding around bounds (0 - max 150) */
    fitBounds = (bounds, options = {}, attempts = 0) => {
        if (!this._model.map) {
            return;
        }

        if (!bounds) {
            return null;
        }
        if (!Array.isArray(bounds) && (!bounds._sw || !bounds._ne)) {
            return null;
        }

        // maplibre divides by the canvas size to compute the camera: on a container that is not laid
        // out yet (hidden panel, ongoing transition) it ends up with a NaN center and throws
        // "Invalid LngLat object: (NaN, NaN)". Wait for the container to be sized instead.
        const canvas = this._model.map.getCanvas();
        if (!canvas || canvas.clientWidth === 0 || canvas.clientHeight === 0) {
            if (attempts < 20) {
                requestAnimationFrame(() => this.fitBounds(bounds, options, attempts + 1));
            }

            return null;
        }

        const boundOptions = {
            // linear: true
        };
        if (Utils.isPresent(options.zoom)) {
            // maximum zoom level to allow
            boundOptions.maxZoom = options.zoom;
        }
        if (Utils.isPresent(options.padding)) {
            boundOptions.padding = options.padding;
            // There is a maximum padding, above 150, fit bounds not working!
            if (boundOptions.padding > 150) {
                boundOptions.padding = 150;
            }

            // A padding that eats the whole canvas leaves maplibre with no room to fit into,
            // which again ends up in a NaN camera on small screens.
            const maxPadding = Math.floor(Math.min(canvas.clientWidth, canvas.clientHeight) / 2) - 1;
            if (boundOptions.padding > maxPadding) {
                boundOptions.padding = Math.max(maxPadding, 0);
            }
        }

        if (Array.isArray(bounds)) {
            if (bounds.length === 0) {
                return null;
            } else if (bounds.length === 2) {
                if (!this._isValidCoordinates(bounds[0]) || !this._isValidCoordinates(bounds[1])) {
                    return null;
                }
            } else if (bounds.length === 4) {
                if (!this._isValidCoordinates([bounds[0], bounds[1]]) || !this._isValidCoordinates([bounds[2], bounds[3]])) {
                    return null;
                }
            } else {
                let polygonBounds = this._buildBounds();
                let validPointCount = 0;

                bounds.forEach((point) => {
                    if (Number.isFinite(point[0]) && Number.isFinite(point[1])) {
                        polygonBounds = polygonBounds.extend([point[0], point[1]]);
                        validPointCount += 1;
                    }
                });

                if (validPointCount === 0) {
                    return null;
                }

                bounds = polygonBounds;
            }
        } else if (bounds && typeof bounds === 'object') {
            if (!this._isValidCoordinates(bounds._sw) || !this._isValidCoordinates(bounds._ne)) {
                return null;
            }
        }

        return this._model.map.fitBounds(bounds, boundOptions);
    };

    getZoom = () => {
        return this._model.map?.getZoom();
    };

    once = (event, callback) => {
        if (!this._model.map) {
            return;
        }

        this._model.map.once(event, callback);
    };

    flyTo = (latLng, zoom, duration) => {
        if (!this._model.map) {
            return;
        }

        if (this._isMapPlannerStyle()) {
            this._model.map.easeTo({
                center: latLng,
                zoom: zoom || this._model.defaultZoom,
                duration: duration || this._model.defaultFlyDuration
            });
        } else {
            // Display directly the point to avoid too many map loads
            this._model.map.jumpTo({
                center: latLng,
                zoom: zoom || this._model.defaultZoom,
                duration: duration || this._model.defaultFlyDuration
            });
        }
    };

    highlightPlaceName = (lat, lng, name) => {
        if (!name) {
            return;
        }

        if (this._model.map.getSource('search-highlight-source')) {
            this._model.map.removeLayer('search-highlight-layer');
            this._model.map.removeSource('search-highlight-source');
        }

        this._model.map.addSource('search-highlight-source', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    properties: {
                        name: name
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat]
                    }
                }]
            }
        });

        this._model.map.addLayer({
            id: 'search-highlight-layer',
            type: 'symbol',
            source: 'search-highlight-source',
            minzoom: 7,
            layout: {
                'text-field': ['get', 'name'],
                'text-size': 18,
                'text-allow-overlap': true
            },
            paint: {
                'text-color': '#bf2113',
                'text-halo-color': 'rgba(255,255,255,0.8)',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.2
            }
        });
    };

    displaySearchMarker = (lat, lng) => {
        if (this._model.searchMarker) {
            this._model.searchMarker.remove();
        }

        if (!this._isValidCoordinates([lat, lng])) {
            return;
        }

        this._model.searchMarker = this.buildMarker('search-marker', {
            lat,
            lng
        }, {
            // No `clickable` option here either: MapLibre's Marker has none. This marker gets its
            // own click listener below.
            draggable: false,
            markerImage: markerIcon
        });

        if (lat && lng && this._model.map) {
            this._model.searchMarker.addTo(this._model.map);

            this._model.searchMarker.getElement()
                .addEventListener('click', function (event) {
                    event.preventDefault();
                    event.stopPropagation();

                    // The body never sees this click: close an open menu here, and keep the marker
                    if (hasOpenMenu()) {
                        closeOpenMenus();
                        return;
                    }

                    if (this._model.searchMarker) {
                        this._model.searchMarker.remove();
                    }
                }.bind(this));

            this._model.searchMarker.getElement()
                .addEventListener('contextmenu', function (event) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (this._model.searchMarker) {
                        const searchCoordinates = this._model.searchMarker.getLngLat();

                        Notification.info(I18n.t('js.maps.marker.share', {url: `${document.location.origin}?marker=true&lat=${searchCoordinates.lat}&lng=${searchCoordinates.lng}`}), false);

                        this._model.searchMarker.remove();
                    }
                }.bind(this));
        }
    };

    toggleLoader = (message) => {
        const $mapArea = this._model.$mapId.querySelector('.map-area');
        const $mapLoader = $mapArea.querySelector('.map-loader');
        if (!$mapLoader) {
            return;
        }
        const $mapLoaderContent = $mapArea.querySelector('.map-loader-content');
        if (!$mapLoader) {
            return;
        }

        if (Utils.isPresent(message)) {
            if ($mapLoaderContent) {
                $mapLoaderContent.textContent = message;
            }
            $mapLoader.style.display = 'block';
        } else {
            $mapLoader.style.display = 'none';
        }
    };

    getZoomDictionary = (zoomLevel) => {
        zoomLevel = Math.floor(zoomLevel);

        if (zoomLevel >= 17) {
            return {
                ghostDist: 20,
                stepDist: 20,
                directionArrow: 200
            };
        } else if (zoomLevel === 16) {
            return {
                ghostDist: 40,
                stepDist: 80,
                directionArrow: 200
            };
        } else if (zoomLevel === 15) {
            return {
                ghostDist: 50,
                stepDist: 200,
                directionArrow: 300
            };
        } else if (zoomLevel === 14) {
            return {
                ghostDist: 80,
                stepDist: 300,
                directionArrow: 500
            };
        } else if (zoomLevel === 13 || zoomLevel === 12) {
            return {
                ghostDist: 100,
                stepDist: 500,
                directionArrow: 750
            };
        } else {
            return {
                ghostDist: null,
                stepDist: 1000,
                directionArrow: null
            };
        }
    };

    isDataChangedOnZoom = (zoomLevel, previousZoomLevel, arrowOnly) => {
        const previousDictionary = this.getZoomDictionary(previousZoomLevel);
        const currentDictionary = this.getZoomDictionary(zoomLevel);

        const ghostDistChanged = previousDictionary.ghostDist !== currentDictionary.ghostDist;
        const stepDistChanged = previousDictionary.stepDist !== currentDictionary.stepDist;
        const directionArrowChanged = previousDictionary.directionArrow !== currentDictionary.directionArrow;

        if (arrowOnly) {
            return directionArrowChanged;
        } else {
            return ghostDistChanged || stepDistChanged || directionArrowChanged;
        }
    };

    distHaversine = (p1, p2, decimal = 6) => {
        const rad = (x) => x * MapInterface.DEG_TO_RAD;

        const lat1 = p1.lat;
        const lng1 = p1.lng;
        const lat2 = p2.lat;
        const lng2 = p2.lng;

        const dLat = rad(lat2 - lat1);
        const dLong = rad(lng2 - lng1);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = MapInterface.EARTH_RADIUS * c;

        // return distance in meters
        return parseFloat((distance * 1000).toFixed(decimal));
    };

    addMapTrackLine = (trackLineSourceId, lineProps = {}) => {
        if (!this._model.map) {
            return;
        }

        if (this._model.map.getSource(trackLineSourceId)) {
            return trackLineSourceId;
        }

        this._model.map.addSource(trackLineSourceId, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });
        this._model.mapSources.push(trackLineSourceId);

        // this._model.map.addLayer({
        //     id: trackLineSourceId,
        //     type: 'line',
        //     source: trackLineSourceId,
        //     layout: {
        //         'line-join': 'round',
        //         'line-cap': 'round'
        //     },
        //     paint: {
        //         'line-color': this._model.mapTrackLineColor,
        //         'line-width': 5,
        //         'line-opacity': 0.75
        //     }
        // });
        // this._model.mapLayers.push(trackLineSourceId);

        // Add line with outline
        [-1, 0, 1, 0].forEach((x, i) => {
            const trackName = trackLineSourceId + '-' + i;

            // Display layer after symbols (use the first symbol id to position this layer below the other layers)
            this._model.map.addLayer({
                id: trackName,
                type: 'line',
                source: trackLineSourceId,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': i === 3
                        ? (lineProps.outlineColor || this._model.mapTrackOutlineColor)
                        : (lineProps.color || this._model.mapTrackLineColor),
                    'line-width': i === 3 ? 5 : 3,
                    'line-offset': x * 3,
                    'line-opacity': lineProps.opacity || 1
                }
            }, this._model.mapLayers.first());

            this._model.mapLayers.push(trackName);
        });

        // Optional overlay marking freehand segments that couldn't be matched to a road: drawn on
        // top of the base line as alternating green / light-green dashes (two complementary dashed
        // layers on the same source) so the user sees they aren't snapped, while staying on-theme.
        if (lineProps.offRoadOverlay) {
            const offRoadSourceId = trackLineSourceId + '-offroad';

            if (!this._model.map.getSource(offRoadSourceId)) {
                this._model.map.addSource(offRoadSourceId, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: []
                    }
                });
                this._model.mapSources.push(offRoadSourceId);

                // Bottom dashes (dark green) + top dashes filling the gaps (light green) -> the line
                // reads as alternating green / lighter-green dashes.
                [
                    {
                        id: offRoadSourceId,
                        color: this._model.mapTrackLineColor,
                        dashArray: [2, 2]
                    },
                    {
                        id: offRoadSourceId + '-alt',
                        color: this._model.mapTrackOutlineColor,
                        dashArray: [0, 2, 2, 0]
                    }
                ].forEach((dashLayer) => {
                    this._model.map.addLayer({
                        id: dashLayer.id,
                        type: 'line',
                        source: offRoadSourceId,
                        layout: {
                            'line-cap': 'butt',
                            'line-join': 'round'
                        },
                        paint: {
                            'line-color': dashLayer.color,
                            'line-width': 4,
                            'line-dasharray': dashLayer.dashArray
                        }
                    }, this._model.mapLayers.first());

                    this._model.mapLayers.push(dashLayer.id);
                });
            }
        }

        return trackLineSourceId;
    };

    static SLOPE_COLOR_EXPRESSION = [
        'step', ['get', 'slope'],
        SLOPE_BANDS[0].color,
        ...SLOPE_BANDS.slice(1).flatMap((band) => [band.min, band.color])
    ];

    buildSlopeGeoJSON = (coordinateElevations) => {
        const features = [];
        for (let i = 1; i < coordinateElevations.length; i++) {
            const prev = coordinateElevations[i - 1];
            const curr = coordinateElevations[i];
            if (curr.deltaDistance <= 0) continue;
            const slope = Math.abs((curr.elevation - prev.elevation) / curr.deltaDistance * 100);
            features.push({
                type: 'Feature',
                properties: {slope},
                geometry: {
                    type: 'LineString',
                    coordinates: [[prev.lng, prev.lat], [curr.lng, curr.lat]]
                }
            });
        }
        return {
            type: 'FeatureCollection',
            features
        };
    };

    setSlopePercentageMode = (trackLineSourceId, enabled) => {
        if (!this._model.map) {
            return;
        }

        [0, 1, 2, 3].forEach((i) => {
            const layerId = trackLineSourceId + '-' + i;
            if (!this._model.map.getLayer(layerId)) {
                return;
            }

            if (enabled) {
                if (i === 3) {
                    this._model.map.setPaintProperty(layerId, 'line-color', 'rgba(50, 50, 50, 0.25)', STYLE_SETTER_OPTIONS);
                } else {
                    this._model.map.setPaintProperty(layerId, 'line-color', MapInterface.SLOPE_COLOR_EXPRESSION, STYLE_SETTER_OPTIONS);
                }
            } else {
                // No STYLE_SETTER_OPTIONS here: these colours come from ride.color, a free-form
                // string with no server-side format validation. maplibre's validation is what turns
                // an unparseable colour into a refused set + ErrorEvent; without it the value
                // reaches the paint pipeline as `undefined`.
                this._model.map.setPaintProperty(
                    layerId,
                    'line-color',
                    i === 3 ? this._model.mapTrackOutlineColor : this._model.mapTrackLineColor
                );
            }
        });
    };

    _darkenHexColor = (hex, ratio = 0.3) => {
        const parsed = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
        if (!parsed) {
            return hex;
        }

        const scaled = [1, 2, 3]
            .map((group) => Math.round(parseInt(parsed[group], 16) * (1 - ratio)))
            .map((channel) => channel.toString(16).padStart(2, '0'));

        return '#' + scaled.join('');
    };

    buildSectionColorGeoJSON = (linePoints, sections) => {
        const stylesForSegment = (endIndex) => {
            if (sections && sections.length) {
                for (let s = 0; s < sections.length; s++) {
                    const section = sections[s];
                    if (endIndex > section.from && endIndex <= section.to) {
                        return {
                            coreColor: section.color,
                            borderColor: this._darkenHexColor(section.color)
                        };
                    }
                }
            }

            return {
                coreColor: this._model.mapTrackOutlineColor,
                borderColor: this._model.mapTrackLineColor
            };
        };

        const features = [];
        for (let i = 1; i < linePoints.length; i++) {
            features.push({
                type: 'Feature',
                properties: stylesForSegment(i),
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [linePoints[i - 1].latLng.lng, linePoints[i - 1].latLng.lat],
                        [linePoints[i].latLng.lng, linePoints[i].latLng.lat]
                    ]
                }
            });
        }

        return {
            type: 'FeatureCollection',
            features
        };
    };

    setSectionColorMode = (trackLineSourceId, enabled, sectionGeoJSON) => {
        if (!this._model.map) {
            return;
        }

        if (enabled && sectionGeoJSON) {
            this.updateSource(trackLineSourceId, sectionGeoJSON);
        }

        [0, 1, 2, 3].forEach((i) => {
            const layerId = trackLineSourceId + '-' + i;
            if (!this._model.map.getLayer(layerId)) {
                return;
            }

            if (enabled) {
                // Layer 3 is the wider core; 0-2 form the border. Recolour both from the section so
                // the whole track takes the chosen colour (core + slightly darker border).
                this._model.map.setPaintProperty(
                    layerId,
                    'line-color',
                    i === 3 ? ['get', 'coreColor'] : ['get', 'borderColor'],
                    STYLE_SETTER_OPTIONS
                );
            } else {
                // No STYLE_SETTER_OPTIONS here: these colours come from ride.color, a free-form
                // string with no server-side format validation. maplibre's validation is what turns
                // an unparseable colour into a refused set + ErrorEvent; without it the value
                // reaches the paint pipeline as `undefined`.
                this._model.map.setPaintProperty(
                    layerId,
                    'line-color',
                    i === 3 ? this._model.mapTrackOutlineColor : this._model.mapTrackLineColor
                );
            }
        });
    };

    buildSurfaceGeoJSON = (linePoints) => {
        const features = [];
        for (let i = 1; i < linePoints.length; i++) {
            const prev = linePoints[i - 1];
            const curr = linePoints[i];
            const surfaceGroup = MapInterface.getSurfaceGroup(curr.surface || prev.surface);
            features.push({
                type: 'Feature',
                properties: {surfaceGroup},
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [prev.latLng.lng, prev.latLng.lat],
                        [curr.latLng.lng, curr.latLng.lat]
                    ]
                }
            });
        }
        return {
            type: 'FeatureCollection',
            features
        };
    };

    setSurfaceTypeMode = (trackLineSourceId, enabled, surfaceGeoJSON) => {
        if (!this._model.map) {
            return;
        }

        const surfaceSourceId = trackLineSourceId + '-surface';

        if (enabled) {
            // Hide track layers 0-3
            [0, 1, 2, 3].forEach((i) => {
                const layerId = trackLineSourceId + '-' + i;
                if (this._model.map.getLayer(layerId)) {
                    this._model.map.setLayoutProperty(layerId, 'visibility', 'none', STYLE_SETTER_OPTIONS);
                }
            });

            // Add or update surface source
            if (this._model.map.getSource(surfaceSourceId)) {
                this._model.map.getSource(surfaceSourceId).setData(surfaceGeoJSON);
            } else {
                this._model.map.addSource(surfaceSourceId, {
                    type: 'geojson',
                    data: surfaceGeoJSON
                });
            }

            // Add outline + main layer per surface group
            for (const [group, config] of Object.entries(MapInterface.SURFACE_GROUPS)) {
                const outlineId = surfaceSourceId + '-' + group + '-outline';
                const mainId = surfaceSourceId + '-' + group;
                const filter = ['==', ['get', 'surfaceGroup'], group];

                // Outline layer (wider, lighter)
                if (!this._model.map.getLayer(outlineId)) {
                    this._model.map.addLayer({
                        id: outlineId,
                        type: 'line',
                        source: surfaceSourceId,
                        filter,
                        layout: {
                            'line-cap': 'round',
                            'line-join': 'round'
                        },
                        paint: {
                            'line-color': config.color,
                            'line-width': 8,
                            'line-opacity': 0.3
                        }
                    });
                }

                // Main layer
                if (!this._model.map.getLayer(mainId)) {
                    this._model.map.addLayer({
                        id: mainId,
                        type: 'line',
                        source: surfaceSourceId,
                        filter,
                        layout: {
                            'line-cap': 'round',
                            'line-join': 'round'
                        },
                        paint: {
                            'line-color': config.color,
                            'line-width': 5,
                            'line-opacity': 1
                        }
                    });
                }

                this._model.map.setLayoutProperty(outlineId, 'visibility', 'visible', STYLE_SETTER_OPTIONS);
                this._model.map.setLayoutProperty(mainId, 'visibility', 'visible', STYLE_SETTER_OPTIONS);
            }
        } else {
            // Show track layers 0-3
            [0, 1, 2, 3].forEach((i) => {
                const layerId = trackLineSourceId + '-' + i;
                if (this._model.map.getLayer(layerId)) {
                    this._model.map.setLayoutProperty(layerId, 'visibility', 'visible', STYLE_SETTER_OPTIONS);
                }
            });

            // Hide surface layers
            for (const group of Object.keys(MapInterface.SURFACE_GROUPS)) {
                const outlineId = surfaceSourceId + '-' + group + '-outline';
                const mainId = surfaceSourceId + '-' + group;
                if (this._model.map.getLayer(outlineId)) {
                    this._model.map.setLayoutProperty(outlineId, 'visibility', 'none', STYLE_SETTER_OPTIONS);
                }
                if (this._model.map.getLayer(mainId)) {
                    this._model.map.setLayoutProperty(mainId, 'visibility', 'none', STYLE_SETTER_OPTIONS);
                }
            }
        }
    };

    removeSurfaceLayers = () => {
        if (!this._model.map) {
            return;
        }

        const surfaceSourceId = this._model.mapTrackLineName + '-surface';

        for (const group of Object.keys(MapInterface.SURFACE_GROUPS)) {
            const mainId = surfaceSourceId + '-' + group;
            const outlineId = surfaceSourceId + '-' + group + '-outline';
            if (this._model.map.getLayer(mainId)) {
                this._model.map.removeLayer(mainId);
            }
            if (this._model.map.getLayer(outlineId)) {
                this._model.map.removeLayer(outlineId);
            }
        }

        if (this._model.map.getSource(surfaceSourceId)) {
            this._model.map.removeSource(surfaceSourceId);
        }
    };

    updateSource = (sourceId, data) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceId);
        if (!currentSource) {
            return;
        }

        currentSource.setData(data);

        return currentSource;
    };

    hasSourceData = (sourceName) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceName);
        if (!currentSource) {
            return false;
        }

        const sourceData = currentSource.serialize();
        const data = sourceData.data;

        if (!data) {
            return false;
        }

        if (data.type === 'FeatureCollection') {
            return !!data.features &&
                !!data.features.length &&
                data.features[0].geometry.coordinates &&
                data.features[0].geometry.coordinates[0] !== 0 &&
                data.features[0].geometry.coordinates[1] !== 0;
        } else if (data.type === 'Feature' && data.geometry?.type === 'LineString') {
            return !!data.geometry && !!data.geometry.coordinates && data.geometry.coordinates.length > 1;
        } else if (data.type === 'Feature' && data.geometry?.type === 'Point') {
            return !!data.geometry &&
                !!data.geometry.coordinates &&
                data.geometry.coordinates.length > 0 &&
                data.geometry.coordinates[0] !== 0 &&
                data.geometry.coordinates[1] !== 0;
        }

        return false;
    };

    resetMapSource = (sourceName, remove) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceName);
        if (!currentSource) {
            return;
        }

        if (remove) {
            if (sourceName.includes('track-line')) {
                [0, 1, 2, 3].forEach((index) => {
                    if (this._model.map.getLayer(`${sourceName}-${index}`)) {
                        this._model.map.removeLayer(`${sourceName}-${index}`);
                    }

                    // this._model.mapLayers = this._model.mapLayers.filter((layer) => layer !== `${sourceName}-${index}`);
                });
            }

            if (this._model.map.getLayer(sourceName)) {
                this._model.map.removeLayer(sourceName);

                // this._model.mapLayers = this._model.mapLayers.filter((layer) => layer !== sourceName);
            }

            this._model.map.removeSource(sourceName);
        } else {
            const sourceData = currentSource.serialize();
            const dataType = sourceData.data?.type;

            if (dataType === 'FeatureCollection') {
                currentSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            } else if (dataType === 'Feature') {
                currentSource.setData({
                    type: 'Feature'
                });
            } else {
                throw new Error('Unknown data type to reset: ' + dataType);
            }
        }
    };

    buildMarker = (markerClassName, latLng, markerOptions) => {
        if (!this._isValidCoordinates(latLng)) {
            return;
        }

        const markerElement = document.createElement('div');
        markerElement.className = markerClassName;

        if (markerOptions) {
            if (markerOptions.markerContent) {
                markerElement.innerHTML = markerOptions.markerContent;
            }
            if (markerOptions.markerImage) {
                markerElement.style.backgroundImage = `url('${markerOptions.markerImage}')`;
            }
            if (markerOptions.markerOpacity) {
                markerElement.style.opacity = markerOptions.markerOpacity;
            }
        }

        const marker = new Marker({element: markerElement, ...markerOptions}).setLngLat(latLng);

        if (markerOptions && markerOptions.draggable) {
            makeMarkerKeyboardDraggable(
                this._model.map,
                marker,
                markerElement,
                I18n.t('js.maps.poi.plan.drag_hint')
            );
        }

        return marker;
    };

    buildPopup = (options = {}) => {
        return new Popup({
            ...options,
            closeButton: false,
            closeOnClick: options.closeOnClick ?? true,
            maxWidth: (options.maxWidth || 320) + 'px',
            offset: options.offset || [0, -10]
        });
    };

    closeAllPopups = () => {
        // The guard that swallows the next map click reads this flag (buildMap._addKeyPointAtClickLoc):
        // closing the popups without clearing it would eat a legitimate point.
        this._model.hasPopup = false;

        // Close previous marker popups
        if (this._model.openPopups.length) {
            for (let previousPopupIndex = 0; previousPopupIndex < this._model.openPopups.length; previousPopupIndex++) {
                const previousPopup = this._model.openPopups[previousPopupIndex];

                if (previousPopup) {
                    previousPopup.remove();
                }
            }

            this._model.openPopups = [];
        }
    };

    manageMarkerPopup = (markerId, onMarkerClick, onMarkerHover, onMarkerLeave) => {
        // Manage click on marker
        const sourceName = markerId;

        this._model.map.on('click', sourceName, (event) => {
            // A click that closes an open menu only closes it: let it reach document.body
            if (hasOpenMenu()) {
                return;
            }

            event.originalEvent.stopPropagation();

            if (onMarkerClick) {
                onMarkerClick(this._convertPopupCoordinates(event), event.features[0].properties);
            }
        });

        // Manage mouse hover on marker
        if (onMarkerHover) {
            this._model.map.on('mouseenter', sourceName, (event) => {
                this._model.map.getCanvas().style.cursor = 'pointer';

                onMarkerHover(this._convertPopupCoordinates(event), event.features[0].properties);
            });
        }

        if (onMarkerLeave) {
            this._model.map.on('mouseleave', sourceName, () => {
                if (!this._model.map) {
                    return;
                }

                this._model.map.getCanvas().style.cursor = '';

                onMarkerLeave();
            });
        }

        // Manage click on marker cluster
        if (this._model.clusterMode) {
            this._model.map.on('click', `${sourceName}-cluster`, (event) => {
                // Manage clusters
                this._manageCluster(markerId, event.point);
            });
        }
    };

    // isHillshadeAvailable = () => {
    //     const currentMapPosition = this._getCurrentMapPosition();
    //
    //     if (!currentMapPosition && !currentMapPosition.center) {
    //         return true;
    //     }
    //
    //     const initialPoint;
    //
    //     if (Array.isArray(currentMapPosition.center)) {
    //         initialPoint = [currentMapPosition.center[0], currentMapPosition.center[1]];
    //     } else {
    //         initialPoint = [currentMapPosition.center.lng, currentMapPosition.center.lat];
    //     }
    //
    //     const europeBounds = new LngLatBounds(MapInterface.europeMaxBounds);
    //     const northAmericaBounds = new LngLatBounds(MapInterface.northAmericaMaxBounds);
    //
    //     return !!(europeBounds.contains(initialPoint) || northAmericaBounds.contains(initialPoint));
    // };

    selectRoutingService = (coordinate) => {
        const firstPoint = [coordinate.lng, coordinate.lat];
        const europeBounds = new LngLatBounds(MapInterface.europeMaxBounds);
        const northAmericaBounds = new LngLatBounds(MapInterface.northAmericaMaxBounds);

        let routingService = MapInterface.routingService.MAP_PLANNER;
        if (!europeBounds.contains(firstPoint) && !northAmericaBounds.contains(firstPoint)) {
            routingService = MapInterface.routingService.MAPBOX;
        }

        return routingService;
    };

    isMapPlannerRouting = (routingService) => {
        return !routingService || routingService === MapInterface.routingService.MAP_PLANNER;
    };

    setMapStyle = (newStyle) => {
        this._handleMapStyles({
            newStyle,
            onNewStyleLoaded: () => {
                this._initializeSourcesAndLayers(() => {
                    if (this._model.waymarkedTrailsEnabled) {
                        this.setWaymarkedTrailsMode(true);
                    }
                    if (this._onMapStyleChanged) {
                        this._onMapStyleChanged();
                    }
                });
            }
        });
    };

    onUnitChanged = (newUnit) => {
        if (!this._model.map) {
            return;
        }

        this._model.distanceUnits = newUnit;

        if (this._model.map.getSource('contours')) {
            this._model.map.removeLayer('contour_lines');
            this._model.map.removeLayer('contour_labels');
            this._model.map.removeSource('contours');
        }

        if (this._model.mapStyleName === 'map_planner_outdoor') {
            this._loadContours();
        }

        this._updateUnitOnMap();

        if (this._onMapStyleChanged) {
            this._onMapStyleChanged();
        }
    };

    getDefaultTrackColor = () => this._model.mapTrackLineColor;

    static WAYMARKED_TRAILS = {
        sourceLayer: 'transportation',
        casingId: 'waymarked-trails-casing',
        color: '#aa5324',
        minzoom: 13,
        dimLayerIds: ['road_path_pedestrian', 'road_path_network'],
        dimOpacity: 0.6
    };

    static HIGHLIGHT_SUBCLASSES = {
        parking: ['parking', 'parking_space'],
        viewpoint: ['viewpoint'],
        drinking_water: ['drinking_water', 'water_point', 'watering_place', 'spring'],
        peak: ['peak'],
        refuge: ['alpine_hut', 'wilderness_hut'],
        shelter: ['shelter'],
        camp_site: ['camp_site', 'caravan_site'],
        pass: ['saddle', 'mountain_pass']
    };

    // Kinds our tiles carry in `class` (subclass empty or 'yes'), as the style and the mobile match them.
    static HIGHLIGHT_CLASSES = {
        drinking_water: ['drinking_water', 'watering_place', 'water_point'],
        peak: ['peak'],
        pass: ['saddle', 'mountain_pass']
    };

    // Summits and passes live in their own source-layer, which has no subclass field.
    static HIGHLIGHT_SOURCE_LAYERS = {
        peak: 'mountain_peak',
        pass: 'mountain_peak'
    };

    // Sprite icon drawn on top of each highlighted POI (the sprite has no pass icon: halo only).
    static HIGHLIGHT_ICONS = {
        parking: 'parking_11',
        viewpoint: 'viewpoint_15',
        drinking_water: 'drinking_water_11',
        peak: 'mountain_peak_11',
        refuge: 'lodging_11',
        shelter: 'shelter_11',
        camp_site: 'camp_site_11'
    };

    // True for a POI whose subclass, or class, is one of the given values.
    static _highlightMatch = (subclasses, classes) => ['any',
        ['in', ['get', 'subclass'], ['literal', subclasses]],
        ['in', ['get', 'class'], ['literal', classes]]
    ];

    highlightMapElement = (element) => {
        const subclasses = MapInterface.HIGHLIGHT_SUBCLASSES[element];

        if (!subclasses || !this._model.map) {
            return;
        }

        const isPmapsStyle = this._model.mapStyleName === MapTileService.layers.MAP_PLANNER
            || this._model.mapStyleName === MapTileService.layers.MAP_PLANNER_OUTDOOR;
        if (!isPmapsStyle) {
            return;
        }

        const haloId = 'highlight-halo-' + element;
        const iconId = 'highlight-icon-' + element;

        // Toggle off if already highlighted.
        if (this._model.map.getLayer(haloId)) {
            this._model.map.removeLayer(haloId);
            if (this._model.map.getLayer(iconId)) {
                this._model.map.removeLayer(iconId);
            }
            this._applyHighlightIconSize();
            return;
        }

        // Insert the halo beneath the POI icons so the icons stay readable on top of the glow.
        const style = this._model.map.getStyle();
        const firstPoiSymbol = style && style.layers
            ? style.layers.find((layer) => layer.type === 'symbol' && /poi/i.test(layer['source-layer'] || ''))
            : null;

        const sourceLayer = MapInterface.HIGHLIGHT_SOURCE_LAYERS[element] || 'poi';
        const filter = ['all', ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]], MapInterface._highlightMatch(subclasses, MapInterface.HIGHLIGHT_CLASSES[element] || [])];

        this._model.map.addLayer({
            id: haloId,
            type: 'circle',
            source: 'map-planner',
            'source-layer': sourceLayer,
            filter,
            paint: {
                'circle-radius': 26,
                'circle-color': '#c82b08',
                'circle-opacity': 0.28,
                'circle-blur': 0.5
            }
        }, firstPoiSymbol ? firstPoiSymbol.id : undefined);

        // The style's POI layers start at z15 for some kinds and let collisions hide icons: draw the
        // highlighted ones on a layer of our own, from any zoom and always shown, above the style's POIs.
        const icon = MapInterface.HIGHLIGHT_ICONS[element];
        if (icon && style && style.layers) {
            let lastPoiIndex = -1;
            style.layers.forEach((layer, index) => {
                if (layer.type === 'symbol' && /poi|mountain_peak/i.test(layer['source-layer'] || '')) {
                    lastPoiIndex = index;
                }
            });
            const abovePoi = lastPoiIndex >= 0 ? style.layers[lastPoiIndex + 1] : null;

            this._model.map.addLayer({
                id: iconId,
                type: 'symbol',
                source: 'map-planner',
                'source-layer': sourceLayer,
                filter,
                layout: {
                    'icon-image': icon,
                    'icon-size': 1.6,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true
                }
            }, abovePoi ? abovePoi.id : undefined);
        }

        // Enlarge the highlighted POIs' icons so they pop, as the legacy per-layer highlight did.
        this._applyHighlightIconSize();

        // The highlighted POIs (peaks, refuges, parking…) only enter the vector tiles at ~zoom 14, so
        // zoom in when the current view is too coarse — otherwise the halo has nothing to render.
        if (this._model.map.getZoom() < 14) {
            this._model.map.flyTo({
                zoom: 14,
                speed: 1.2,
                curve: 1.42,
                essential: true
            });
        }
    };

    // Grow the icons of every currently-highlighted subclass (data-driven per POI symbol layer), and
    // restore each layer's original icon-size once no element is highlighted anymore.
    _applyHighlightIconSize = () => {
        const map = this._model.map;
        const style = map && map.getStyle();
        if (!style || !style.layers) {
            return;
        }

        const active = [];
        const activeClasses = [];
        Object.keys(MapInterface.HIGHLIGHT_SUBCLASSES).forEach((element) => {
            if (map.getLayer('highlight-halo-' + element)) {
                active.push(...MapInterface.HIGHLIGHT_SUBCLASSES[element]);
                activeClasses.push(...(MapInterface.HIGHLIGHT_CLASSES[element] || []));
            }
        });

        this._model.highlightOriginalIconSize ||= {};

        style.layers.forEach((layer) => {
            if (layer.type !== 'symbol' || !/poi/i.test(layer['source-layer'] || '') || layer.id.startsWith('highlight-') || !map.getLayer(layer.id)) {
                return;
            }

            // Capture the untouched original size once, so it can be restored exactly later.
            if (!(layer.id in this._model.highlightOriginalIconSize)) {
                const current = map.getLayoutProperty(layer.id, 'icon-size');
                this._model.highlightOriginalIconSize[layer.id] = current === undefined ? 1 : current;
            }
            const original = this._model.highlightOriginalIconSize[layer.id];

            // A legacy stop-function object can't be embedded inside a 'case' expression, so only
            // layers whose size is a plain number or an expression get the enlargement.
            const canEnlarge = typeof original === 'number' || Array.isArray(original);

            if (active.length) {
                if (canEnlarge) {
                    // No STYLE_SETTER_OPTIONS: `original` is read back from the style and may be a
                    // zoom interpolation, which is invalid nested inside 'case'. With validation on,
                    // the layer is skipped and the loop goes on; without it, setValue THROWS and
                    // aborts the whole pass.
                    map.setLayoutProperty(layer.id, 'icon-size', ['case', MapInterface._highlightMatch(active, activeClasses), 1.6, original]);
                }
            } else {
                map.setLayoutProperty(layer.id, 'icon-size', original, STYLE_SETTER_OPTIONS);
            }
        });
    };

    // Highlight officially waymarked hiking routes (network=iwn/nwn/rwn/lwn) from the vector tiles,
    // so hikers can see — and stick to — marked trails. The data only exists in the MapPlanner
    // ('map-planner') tiles, so this degrades to a no-op on third-party raster styles.
    setWaymarkedTrailsMode = (enabled) => {
        if (!this._model.map) {
            return;
        }

        // Remember the intent regardless of the current style, so it can be re-applied when the
        // user switches back to a pmaps style (the overlay simply stays hidden meanwhile).
        this._model.waymarkedTrailsEnabled = enabled;

        const isPmapsStyle = this._model.mapStyleName === MapTileService.layers.MAP_PLANNER
            || this._model.mapStyleName === MapTileService.layers.MAP_PLANNER_OUTDOOR;

        if (enabled && isPmapsStyle) {
            this._addWaymarkedTrailsLayers();
        } else {
            this._removeWaymarkedTrailsLayers();
        }
    };

    _addWaymarkedTrailsLayers = () => {
        const map = this._model.map;
        const {
            sourceLayer,
            casingId,
            color,
            minzoom,
            dimLayerIds,
            dimOpacity
        } = MapInterface.WAYMARKED_TRAILS;

        if (!map || !map.getSource('map-planner') || map.getLayer(casingId)) {
            return;
        }

        // Keep the waymarked paths at full opacity and fade the rest (data-driven per feature), so the
        // marked trails stand out by contrast. Store each layer's original opacity to restore on disable.
        this._model.waymarkedDimmedLayers = {};
        dimLayerIds.forEach((id) => {
            if (!map.getLayer(id)) {
                return;
            }
            const original = map.getPaintProperty(id, 'line-opacity');
            this._model.waymarkedDimmedLayers[id] = original === undefined ? null : original;
            map.setPaintProperty(id, 'line-opacity', ['case', ['==', ['get', 'hiking_route'], 1], 1, dimOpacity], STYLE_SETTER_OPTIONS);
        });

        // A coloured border drawn just BENEATH the base path line: the trail keeps its natural look in
        // the middle and only gains an outline that peeks at the edges — no solid fill over it. Insert
        // below the base path layer (fall back to the first label if that layer isn't in this style).
        const style = map.getStyle();
        const firstSymbol = style && style.layers
            ? style.layers.find((layer) => layer.type === 'symbol')
            : null;
        const beforeId = dimLayerIds.find((id) => map.getLayer(id)) || (firstSymbol ? firstSymbol.id : undefined);

        map.addLayer({
            id: casingId,
            type: 'line',
            source: 'map-planner',
            'source-layer': sourceLayer,
            minzoom,
            filter: ['==', ['get', 'hiking_route'], 1],
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': color,
                'line-opacity': 0.25,
                'line-width': ['interpolate', ['linear'], ['zoom'], 13, 5, 16, 7, 20, 11]
            }
        }, beforeId);
    };

    _removeWaymarkedTrailsLayers = () => {
        const map = this._model.map;
        if (!map) {
            return;
        }

        // Remove the border (and any legacy line/glow layers from earlier versions).
        [MapInterface.WAYMARKED_TRAILS.casingId, 'waymarked-trails-line', 'waymarked-trails-glow'].forEach((id) => {
            if (map.getLayer(id)) {
                map.removeLayer(id);
            }
        });

        const dimmed = this._model.waymarkedDimmedLayers || {};
        Object.keys(dimmed).forEach((id) => {
            if (map.getLayer(id)) {
                map.setPaintProperty(id, 'line-opacity', dimmed[id] === null ? undefined : dimmed[id], STYLE_SETTER_OPTIONS);
            }
        });
        this._model.waymarkedDimmedLayers = {};
    };

    queryNearbyParkings = (center, radiusMeters) => {
        if (!this._model.map) {
            return [];
        }

        const parkingFilter = [
            'all',
            ['==', 'class', 'amenity'],
            ['in', 'subclass', 'parking']
        ];

        const features = this._model.map.querySourceFeatures('map-planner', {
            sourceLayer: 'poi',
            filter: parkingFilter
        });

        const seen = new Set();
        const results = [];

        features.forEach((feature) => {
            const coords = feature.geometry.type === 'MultiPoint'
                ? feature.geometry.coordinates
                : [feature.geometry.coordinates];

            coords.forEach(([lng, lat]) => {
                const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
                if (seen.has(key)) {
                    return;
                }
                seen.add(key);

                const distance = this.distHaversine(center, {
                    lat,
                    lng
                });
                if (distance > radiusMeters) {
                    return;
                }

                results.push({
                    name: feature.properties.name || feature.properties['name:latin'] || null,
                    lat,
                    lng,
                    distance
                });
            });
        });

        return results.sort((a, b) => a.distance - b.distance);
    };

    changeMapFontSize = (delta) => {
        if (!this._model.map) {
            return;
        }

        // Store original text-size values on first use
        if (!this._model.originalTextSizes) {
            this._model.originalTextSizes = {};
            this._model.mapFontScale = 1;

            const style = this._model.map.getStyle();
            if (!style?.layers) {
                return;
            }

            style.layers.forEach((layer) => {
                if (layer.type === 'symbol' && layer.layout?.['text-size'] !== undefined) {
                    this._model.originalTextSizes[layer.id] = JSON.parse(JSON.stringify(layer.layout['text-size']));
                }
            });
        }

        this._model.mapFontScale = Math.max(0.5, Math.min(2.5, this._model.mapFontScale + delta * 0.1));

        Object.entries(this._model.originalTextSizes).forEach(([layerId, originalSize]) => {
            if (!this._model.map.getLayer(layerId)) {
                return;
            }

            if (typeof originalSize === 'number') {
                this._model.map.setLayoutProperty(layerId, 'text-size', Math.round(originalSize * this._model.mapFontScale), STYLE_SETTER_OPTIONS);
            } else if (Array.isArray(originalSize)) {
                // No STYLE_SETTER_OPTIONS: same reason as icon-size above — `originalSize` is read
                // back from the style, and a zoom interpolation inside '*' is invalid. The Mapbox
                // styles use exactly that form for text-size.
                this._model.map.setLayoutProperty(layerId, 'text-size', ['*', originalSize, this._model.mapFontScale]);
            } else if (originalSize?.stops) {
                // Legacy stops format: {base, stops: [[zoom, size], ...]}
                const scaled = {
                    ...originalSize,
                    stops: originalSize.stops.map(([zoom, size]) => [zoom, Math.round(size * this._model.mapFontScale)])
                };
                this._model.map.setLayoutProperty(layerId, 'text-size', scaled);
            }
        });
    };

    // enterFullscreen = () => {
    //     if (!this._model.map) {
    //         return;
    //     }
    //
    //     if (!document.fullscreenElement && typeof this._model.map.getContainer().requestFullscreen === 'function') {
    //         this._model.map.getContainer()
    //             .requestFullscreen();
    //     }
    // };

    leaveFullscreen = () => {
        if (!this._model.map) {
            return;
        }

        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
            document.exitFullscreen();
        }
    };

    destroy = () => {
        this._model.isDestroyed = true;

        if (!this._model.map) {
            return;
        }

        if (this._model.clearLoadWatchers) {
            this._model.clearLoadWatchers();
        } else {
            clearTimeout(this._model.loadWatchdog);
        }

        this._model.map.remove();
        // Late work (a copied ride's tracks) must not run its load-time setup on the removed map.
        this._model.isMapLoaded = false;

        if (this.hasModule('ELEVATION_COMPUTATION') && this.destroyElevationMap) {
            this.destroyElevationMap();
        }
    };

    /* Private Methods
    ******************** */
    _mapLoaded = (params) => {
        if (!this._model.map || this._model.isMapLoaded) {
            for (let i = 0, n = this._model.mapLoadedCallbacks.length; i < n; i++) {
                try {
                    this._model.mapLoadedCallbacks[i]();
                } catch (error) {
                    pushError(error, {mapId: this._model.mapId});
                }
            }
        } else {
            if (params.mapLoaded) {
                this._model.isMapLoaded = true;
            }

            // (this._model.map.loaded() && this._model.map.isStyleLoaded())

            for (let i = 0, n = this._model.mapLoadedCallbacks.length; i < n; i++) {
                // Isolated: one callback that throws used to stop every later one, so a single
                // failure (a second WebGL context, say) took down the whole post-load setup.
                try {
                    this._model.mapLoadedCallbacks[i]();
                } catch (error) {
                    pushError(error, {mapId: this._model.mapId});
                }
            }
        }
    };

    _isValidCoordinates = (latLng) => {
        let lat;
        let lng;

        if (Array.isArray(latLng)) {
            lat = latLng[0];
            lng = latLng[1];
        } else if (latLng && typeof latLng === 'object') {
            lat = latLng.lat;
            lng = latLng.lng;
        }

        return (Number.isFinite(lat) && Math.abs(lat) <= 90) && (Number.isFinite(lng) && Math.abs(lng) <= 180);
    };

    _isMapPlannerStyle = () => {
        return this._model.mapStyleName === MapTileService.layers.MAP_PLANNER || this._model.mapStyleName === MapTileService.layers.MAP_PLANNER_OUTDOOR;
    };

    _manageMapProviderLimitation = () => {
        this._model.map.setMaxBounds(null);
        this._model.map.setMinZoom(MapInterface.mapMinZoom);
    };

    _handleMapStyles = (mapStyleOptions) => {
        let mapMaxZoom = 18;

        const isNewStyle = !!mapStyleOptions.newStyle;

        // Select layer service
        this._model.mapStyleName = mapStyleOptions.newStyle || MapTileService.getTileService() || MapTileService.layers.MAP_PLANNER;

        this._model.mapStyle = MapTileService.selectTileStyle(this._model.mapStyleName, {
            isNewStyle: isNewStyle
        });

        if (this._model.mapStyleName === 'mapbox_satellite') {
            mapMaxZoom = 17;
        }

        if (this._model.map) {
            this._model.map.setMaxZoom(mapMaxZoom);
        }

        if (!this._model.mapStyle) {
            throw new Error('MAP: No style declared for map');
        }

        if (isNewStyle && this._model.map) {
            this._model.map.setStyle(this._model.mapStyle);

            this._manageMapProviderLimitation();

            // style.load or style.change not working anymore since Maplibre 3
            if (mapStyleOptions.onNewStyleLoaded) {
                this._model.map.once('styledata', () => {
                    mapStyleOptions.onNewStyleLoaded(this._model.mapStyle);
                });
            }
        }

        return mapMaxZoom;
    };

    // Single degradation path for both ways WebGL2 can be unavailable: the upfront probe says no,
    // or the Map constructor throws. Counted in the admin availability rate, not sent to Sentry.
    _reportGlUnavailable = (error) => {
        AnalyticsService.trackWebGLError();
        AnalyticsService.trackMapRender(error ? 'crashed' : 'unsupported');

        // NOT alertPopup: it is only assigned in an onPageReady(…, 2600) block that runs after
        // _createMap, and the constructor returns before it when there is no map — so that branch
        // could never fire and the user was left with a blank map and no explanation. Notification
        // is available at construction time and is already how the map reports its own errors.
        Notification.error(I18n.t('js.maps.errors.not_supported'));

        return false;
    };

    // MapLibre's built-in control labels, in the page locale.
    _controlsLocale = () => ({
        'NavigationControl.ZoomIn': I18n.t('js.a11y.map_zoom_in'),
        'NavigationControl.ZoomOut': I18n.t('js.a11y.map_zoom_out'),
        'NavigationControl.ResetBearing': I18n.t('js.a11y.map_reset_bearing'),
        'FullscreenControl.Enter': I18n.t('js.maps.toolbar.fullscreen.enter'),
        'FullscreenControl.Exit': I18n.t('js.maps.toolbar.fullscreen.exit'),
        'GeolocateControl.FindMyLocation': I18n.t('js.maps.geolocation.locate_me'),
        'GeolocateControl.LocationNotAvailable': I18n.t('js.a11y.map_location_unavailable'),
        'Map.Title': I18n.t('js.a11y.map_title'),
        'CooperativeGesturesHandler.WindowsHelpText': I18n.t('js.a11y.map_gestures_windows'),
        'CooperativeGesturesHandler.MacHelpText': I18n.t('js.a11y.map_gestures_mac'),
        'CooperativeGesturesHandler.MobileHelpText': I18n.t('js.a11y.map_gestures_mobile')
    });

    _createMap = (mapParams) => {
        const mapMaxZoom = this._handleMapStyles({
            newMapCenter: mapParams.center
        });

        // Check if GL is supported
        if (!isSupported()) {
            return this._reportGlUnavailable();
        }

        const pmtilesProtocol = new PMTilesProtocol();
        addProtocol('pmtiles', pmtilesProtocol.tile);

        // maplibre-gl 6.7 THROWS from the constructor when the WebGL2 context cannot be created,
        // where it used to fire an uncatchable error event. isSupported() above no longer covers
        // this: mapbox-gl-supported 4.1 caches its probe, so it keeps answering true after the
        // page has exhausted its contexts.
        try {
            this._model.map = new MapLibre({
                container: mapParams.mapId,
                minZoom: 3,
                maxZoom: mapMaxZoom,
                center: mapParams.center,
                bounds: mapParams.boundingBox,
                fitBoundsOptions: mapParams.boundingBox ? {padding: 80} : undefined,
                zoom: mapParams.zoom,
                attributionControl: false,
                locale: this._controlsLocale(),
                // Display labels/icons immediately instead of cross-fading them in,
                // for a faster initial render.
                fadeDuration: 0,
                // Force to use CTRL ou 2 fingers to move the map
                cooperativeGestures: mapParams.gestureEnabled || false,
                transformRequest: (url, resourceType) => {
                    // Fast path: the MapPlanner styles never hit Mapbox endpoints,
                    // so skip the per-request URL checks on the hot tile path.
                    if (this._isMapPlannerStyle()) {
                        return {url};
                    }

                    if (url?.includes('/v1/mapbox.mapbox-terrain-dem-v1/')) {
                        url = url.replace('https://a.tiles.mapbox.com/raster/v1/mapbox.mapbox-terrain-dem-v1', 'https://api.mapbox.com/raster/v1/mapbox.mapbox-terrain-dem-v1');
                        url = url.replace('.png?', '.webp?sku=101pwRKmRS10b&');
                    } else if (isMapboxURL(url)) {
                        return transformMapboxUrl(url, resourceType, MapTileService.mapboxAccessToken);
                    }

                    return {
                        url: url
                    };
                }
            });
        } catch (error) {
            return this._reportGlUnavailable(error);
        }

        AnalyticsService.trackMapRender('ok');

        // First style only: the relief (hillshade, colour relief) comes after the first display, its tiles
        // being the heaviest of the first screen. A later style switch loads it as usual.
        let deferredDemLayerIds = [];
        this._model.map.setStyle(this._model.mapStyle, {
            transformStyle: (_previousStyle, nextStyle) => {
                const {style, hiddenLayerIds} = deferDemLayers(nextStyle);
                deferredDemLayerIds = hiddenLayerIds;

                return style;
            }
        });
        this._model.map.once('idle', () => {
            if (this._model.map) {
                revealLayers(this._model.map, deferredDemLayerIds);
            }
        });

        // Increase zoom speed on wheel for touchpad, (default 1 / 100)
        this._model.map.scrollZoom?.setZoomRate(1 / 25);

        // Map scale
        if (!mapParams.hideMapScale) {
            this._model.map.addControl(new ScaleControl({
                unit: window.unit === 'mi' ? 'imperial' : 'metric'
            }), 'bottom-right');
        }

        // Add zoom and rotation controls buttons
        this._model.map.addControl(new NavigationControl({
            visualizePitch: true,
            showZoom: true,
            showCompass: true
        }));

        // Add a fullscreen control button
        this._model.map.addControl(new FullscreenControl());

        if (mapParams.geolocator) {
            // Add geolocate control to the map
            this._model.map.addControl(mapParams.geolocator);
        }

        // Add 3D view control button
        this._model.map.addControl(new Terrain3DControl({
            onClick: this._toggle3DTerrain,
            showProductModal: this._model.showProductModal
        }));

        // Set the map's max bounds and zoom for MapPlanner style
        this._manageMapProviderLimitation();

        // // Limit map usage
        // MapTileService.monitorMapUsage(this._model.map, mapParams.mapLimitations, this.toggleLoader);

        // Useless
        // mapInterface.map.addControl(
        //     new MapboxGeocoder({
        //         accessToken: maplibregl.accessToken,
        //         maplibregl: maplibregl,
        //         autocomplete: true,
        //         types: 'region,postcode,place,locality,address',
        //         proximity: {},
        //         countries: ''
        //     })
        // );

        return this._model.map;
    };

    _getCurrentMapPosition = () => {
        let mapCenter;
        let mapZoom;
        let mapBoundingBox;
        const userLocation = {
            lat: parseFloat(window.userLatitude) || 0,
            lng: parseFloat(window.userLongitude) || 0
        };
        let urlLocation = (new URL(window.location)).searchParams.get('location');

        if (urlLocation) {
            urlLocation = urlLocation.split(',');
            if (urlLocation[0] && urlLocation[1]) {
                mapCenter = {
                    lat: parseFloat(urlLocation[0]),
                    lng: parseFloat(urlLocation[1])
                };
            }

            if (urlLocation[2]) {
                mapZoom = parseInt(urlLocation[2]);
            }
        } else if (this._model.reusePosition) {
            const position = this._getSavedMapPosition();
            if (position && Utils.isPresent(position.center)) {
                mapCenter = position.center;
                mapZoom = position.zoom > this._model.searchZoom ? this._model.searchZoom : position.zoom;
            }
        } else if (Utils.isPresent(this._model.initialLocation)) {
            if (Array.isArray(this._model.initialLocation)) {
                if (Array.isArray(this._model.initialLocation[0]) && Array.isArray(this._model.initialLocation[1])) {
                    const boundingBox = new LngLatBounds([this._model.initialLocation[0][1], this._model.initialLocation[0][0]], [this._model.initialLocation[1][1], this._model.initialLocation[1][0]]);

                    mapBoundingBox = boundingBox;
                    mapCenter = boundingBox.getCenter();
                    mapZoom = null;
                } else {
                    this._model.initialLat = parseFloat(this._model.initialLocation[0]);
                    this._model.initialLong = parseFloat(this._model.initialLocation[1]);

                    if (this._model.initialLat && this._model.initialLong) {
                        mapCenter = {
                            lat: this._model.initialLat,
                            lng: this._model.initialLong
                        };
                        mapZoom = this._model.initialZoom || this._model.rideZoom;
                    }
                }
            } else if (this._model.initialLocation.center) {
                this._model.initialLat = parseFloat(this._model.initialLocation.center[1]);
                this._model.initialLong = parseFloat(this._model.initialLocation.center[0]);

                if (this._model.initialLat && this._model.initialLong) {
                    mapCenter = {
                        lat: this._model.initialLat,
                        lng: this._model.initialLong
                    };
                    mapZoom = this._model.initialZoom || this._model.rideZoom;
                }
            }
        } else if (Utils.isPresent(this._model.initialLat) && Utils.isPresent(this._model.initialLong)) {
            this._model.initialLat = parseFloat(this._model.initialLat);
            this._model.initialLong = parseFloat(this._model.initialLong);

            mapCenter = {
                lat: this._model.initialLat,
                lng: this._model.initialLong
            };
            mapZoom = this._model.initialZoom || this._model.rideZoom;
        }

        if (Utils.isPresent(mapBoundingBox)) {
            return {
                boundingBox: mapBoundingBox,
                center: mapCenter
            };
        } else if (Utils.isPresent(mapCenter) && Utils.isPresent(mapZoom)) {
            return {
                center: mapCenter,
                zoom: mapZoom
            };
        } else if (userLocation.lat !== 0 && userLocation.lng !== 0) {
            return {
                center: [userLocation.lng, userLocation.lat],
                zoom: MapInterface.userZoom
            };
        } else {
            return {
                center: window.mapLocation.reverse(),
                zoom: MapInterface.countryZoom
            };
        }
    };

    _saveMapPosition = () => {
        if (!this._model.reusePosition) {
            return;
        }

        const mapCenter = this._model.map.getCenter();
        const mapZoom = this._model.map.getZoom();
        StorageService.setItem('MapCenter', mapCenter);
        StorageService.setItem('MapZoom', mapZoom);
    };

    _getSavedMapPosition = () => {
        if (!this._model.reusePosition) {
            return;
        }

        const center = StorageService.getItem('MapCenter');
        const zoom = StorageService.getItem('MapZoom');

        if (Utils.isPresent(center)) {
            return {
                center: center,
                zoom: zoom
            };
        }
    };

    // Load a marker icon on demand when a layer needs it before its bitmap is
    // registered. Bound once; silences MapLibre's "styleimagemissing" warnings.
    _bindStyleImageMissing = () => {
        if (this._model.styleImageMissingBound || !this._model.map) {
            return;
        }

        this._model.styleImageMissingBound = true;
        this._model.pendingMarkerImages ||= {};

        this._model.map.on('styleimagemissing', (event) => {
            const missingId = event.id;
            const url = this._model.markerImageUrls[missingId];

            // Ignore images we don't own (e.g. sprite icons) or already-handled ones
            if (!url || this._model.pendingMarkerImages[missingId] || this._model.map.hasImage(missingId)) {
                return;
            }

            this._model.pendingMarkerImages[missingId] = true;

            // Register a 1×1 transparent placeholder synchronously: MapLibre re-reads the image
            // right after firing this event and warns if it's still missing. The real bitmap is
            // decoded off-thread below and swapped in via updateImage (icons still appear).
            this._model.map.addImage(missingId, {
                width: 1,
                height: 1,
                data: new Uint8Array(4)
            });

            this._model.map.loadImage(url)
                .then((loaded) => {
                    if (this._model.map) {
                        // Swap the placeholder for the real bitmap (updateImage would reject the
                        // size change, so remove then re-add).
                        if (this._model.map.hasImage(missingId)) {
                            this._model.map.removeImage(missingId);
                        }
                        this._model.map.addImage(missingId, loaded.data);
                    }
                    delete this._model.pendingMarkerImages[missingId];
                })
                .catch(() => {
                    delete this._model.pendingMarkerImages[missingId];
                });
        });
    };

    _loadMarkerImages = (loadedCallback, images) => {
        const sourceClusters = [];

        // Remember each marker's bitmap url so a layer that renders before its icon
        // finished decoding can pull it in on demand (avoids "styleimagemissing" warnings)
        this._model.markerImageUrls ||= {};
        images.forEach((image) => {
            if (image.url) {
                this._model.markerImageUrls[image.id] = image.url;
            }
        });

        this._bindStyleImageMissing();

        // Create the sources and layers synchronously so the map is fully
        // interactive (a first click adds a point, markers can receive data)
        // as soon as this returns. The icon bitmaps are decoded afterwards in
        // the background, keeping them off the initial-load critical path.
        images.forEach((image) => {
            const imageProps = image.properties || {};

            if (this._model.map.getSource(image.id)) {
                return;
            }

            // Add a data source containing one point feature
            this._model.mapSources.push(image.id);
            this._model.map.addSource(image.id, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                },
                cluster: imageProps.cluster || false,
                clusterMaxZoom: window.settings.disable_cluster_zoom || 14, // Max zoom to cluster points on
                clusterRadius: window.settings.cluster_radius || 50 // Radius of each cluster when clustering points (defaults to 50)
            });

            // Add a layer to use the image to represent the data
            this._model.mapLayers.push(image.id);
            this._model.map.addLayer({
                id: image.id,
                type: 'symbol',
                source: image.layerSourceId || image.id, // reference the data source
                layout: {
                    // 'text-field': ['get', 'content'],
                    // 'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                    // 'text-radial-offset': 0.5,
                    // 'text-justify': 'auto',
                    'icon-image': image.id, // reference the image
                    'icon-size': typeof imageProps.size === 'undefined' ? 1 : imageProps.size,
                    'icon-offset': typeof imageProps.offset === 'undefined' ? [0, 0] : imageProps.offset,
                    'icon-anchor': imageProps.position || 'center',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'icon-rotate': imageProps.rotation || 0,
                    'icon-rotation-alignment': imageProps.rotationAlignment || 'auto',
                    // 'icon-ignore-placement': true
                    // 'symbol-placement': imageProps.placement || 'point'
                },
                paint: {
                    'icon-opacity': typeof imageProps.opacity === 'undefined' ? 1 : imageProps.opacity
                }
            });

            if (imageProps.cluster) {
                sourceClusters.push({
                    id: image.id,
                    imageProps: imageProps
                });
            }
        });

        // Display clusters above simple markers
        sourceClusters.forEach((image) => {
            this._model.mapLayers.push(image.id + '-cluster');
            this._model.map.addLayer({
                id: image.id + '-cluster',
                type: 'symbol',
                source: image.id,
                layout: {
                    'icon-image': 'cluster-marker', // reference the image
                    'icon-size': typeof image.imageProps.size === 'undefined' ? 1 : image.imageProps.size,
                    'icon-anchor': 'bottom',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'icon-offset': [0, 4],
                    'text-font': ['Roboto Regular'],
                    'text-size': 14,
                    'text-letter-spacing': 0.05,
                    'text-field': ['get', 'point_count'],
                    'text-offset': [0, -1.2] // move the label vertically downwards slightly to improve centering
                },
                paint: {
                    'text-color': 'black'
                },
                filter: ['>', 'point_count', 0]
            });
        });

        // The map is ready: enable interaction now.
        loadedCallback();

        // Decode and register the icon bitmaps in the background. Layers
        // referencing a not-yet-loaded image simply render nothing until the
        // image is added a few milliseconds later.
        if (this._model.map && this._model.map.style) {
            loadMarkerImages(this._model.map, images);
        }
    };

    _initializeSourcesAndLayers = (loadedCallback, initialParams = {}) => {
        // Load contours and hillshade as new source
        if (this._model.mapStyleName === 'map_planner_outdoor') {
            if (this._model.map.getSource('contours')) {
                return;
            }

            // Shifts hillshade and contours loading for performance reason
            setTimeout(() => {
                // this._updateHillshade({
                //     maxZoom: 18,
                //     visibility: 'visible'
                // });
                this._loadContours();
            }, 350);
        } else if (this._model.mapStyleName === 'map_planner') {
            if (this._model.map.getSource('contours')) {
                return;
            }

            // // Set hillshade available for everyone
            // // Shifts hillshade loading for performance reason
            // setTimeout(() => {
            //     this._updateHillshade({
            //         maxZoom: 18,
            //         visibility: 'visible'
            //     });
            // }, 300);
        } else {
            // this._model.map.removeLayer('hillshade');
            // this._model.map.removeLayer('contour_lines');
            // this._model.map.removeLayer('contour_labels');

            // if (this._model.map.getSource('hillshade')) {
            //     this._model.map.removeSource('hillshade');
            // }

            if (this._model.map.getSource('contours')) {
                this._model.map.removeSource('contours');
            }
        }

        this._updateLocaleMap();

        if (this._model.distanceUnits === 'mi') {
            this._updateUnitOnMap();
        }

        // this._updateHillshade({maxZoom: 2});
        // if (!this.isHillshadeAvailable()) {
        //     this._updateHillshade({maxZoom: 2});
        // }

        const defaultMarkers = [
            {
                id: 'arrow-marker',
                url: markerArrowIcon,
                properties: {
                    size: 0.4,
                    opacity: 0.85,
                    rotation: ['get', 'angle'],
                    rotationAlignment: 'map'
                }
            },
            {
                id: 'finish-marker',
                url: markerStopIcon,
                properties: {
                    size: 0.5
                }
            },
            {
                id: 'ride-finish-marker',
                url: markerStopIcon,
                properties: {
                    size: 0.5
                }
            },
            {
                id: 'bike-primary-marker',
                url: markerBikeIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'walk-primary-marker',
                url: markerWalkIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'running-primary-marker',
                url: markerRunningIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'hiking-primary-marker',
                url: markerHikingIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'cluster-marker',
                url: markerBlankIcon,
                properties: {
                    position: 'bottom'
                }
            },
            // {
            //     id: 'realtime-marker',
            //     url: `${window.settings.assets_url}/maps/realtime.png`,
            //     properties: {
            //         position: 'bottom',
            //         offset: [0, 15]
            //     }
            // }
        ];

        // Features are displayed by order of import: last import will be the top layer on map
        // Load first the less important elements
        this._loadMarkerImages(loadedCallback, [
            ...defaultMarkers,
            ...this._model.mapCustomMarkers
        ]);
    };

    _updateLocaleMap = () => {
        if (this._model.mapStyleName !== 'map_planner_outdoor' && this._model.mapStyleName !== 'map_planner') {
            return;
        }

        // Change locale names according to the current locale
        const translatedLabels = ['country_1', 'country_2', 'country_3', 'state', 'place_city', 'place_town', 'place_village', 'place_locality', 'place_other', 'parkname_large', 'parkname_small'];
        translatedLabels.forEach((label) => {
            this._model.map.setLayoutProperty(label, 'text-field', [
                'coalesce',
                ['get', `name:${I18n.locale}`],
                ['get', 'name:latin'],
            ], STYLE_SETTER_OPTIONS);
        });
    };

    // _updateHillshade = (options) => {
    //     if (!options || !this._model.map || !this._model.map.getSource('hillshade')) {
    //         return;
    //     }
    //
    //     if (options.maxZoom) {
    //         this._model.map.setLayerZoomRange('hillshade', 6, options.maxZoom);
    //     }
    //
    //     if (options.visibility) {
    //         this._model.map.setLayoutProperty('hillshade', 'visibility', options.visibility);
    //     }
    // };

    // _loadHillshade = (maxZoom = 24) => {
    //     this._model.map.addSource('hillshade', {
    //         'type': 'raster-dem',
    //         'url': 'https://hillshade.map-planner.com/data/hillshade_12.json'
    //     });
    //
    //     this._model.map.addLayer(
    //         {
    //             id: 'hillshade',
    //             source: 'hillshade',
    //             type: 'hillshade',
    //             minzoom: 6,
    //             maxzoom: maxZoom,
    //             paint: {
    //                 'hillshade-illumination-direction': 335,
    //                 'hillshade-highlight-color': 'rgba(189, 212, 192, 0.8)',
    //                 'hillshade-shadow-color': 'rgba(55, 61, 55, 0.8)',
    //                 'hillshade-accent-color': 'rgba(74, 86, 75, 0.9)',
    //                 'hillshade-exaggeration': [
    //                     'interpolate',
    //                     ['linear'],
    //                     ['zoom'],
    //                     4,
    //                     0.4,
    //                     7,
    //                     0.5,
    //                     10,
    //                     0.65,
    //                     11,
    //                     0.8,
    //                     12,
    //                     1
    //                 ],
    //                 'hillshade-illumination-anchor': 'viewport'
    //             }
    //         },
    //         // Insert hillshade after the following layer ID:
    //         'landcover_ice'
    //     );
    // };

    _loadContours = async () => {
        if (!this._model.map) {
            return;
        }

        const {default: mlcontour} = await import(/* webpackChunkName: "maps-contour" */ 'maplibre-contour');

        if (!this._model.map) {
            return;
        }

        this._model.mapDemSource = new mlcontour.DemSource({
            url: 'https://hillshade.map-planner.com/data/{z}/{x}/{y}.webp',
            encoding: 'mapbox',
            maxzoom: 12,
            worker: true, // offload isoline computation to a web worker to reduce jank
            cacheSize: 300, // number of most-recent tiles to cache
            timeoutMs: 3_000 // timeout on fetch requests
        });

        this._model.mapDemSource.setupMaplibre({addProtocol});

        if (this._model.map.getSource('contours')) {
            return;
        }

        this._model.map.addSource('contours',
            {
                type: 'vector',
                tiles: [
                    this._model.mapDemSource.contourProtocolUrl({
                        // convert meters to feet, default=1 for meters
                        multiplier: this._model.distanceUnits === 'mi' ? 3.28084 : 1,
                        thresholds: {
                            // zoom: [minor, major]
                            // 11: [200, 1000],
                            12: [100, 500],
                            14: [50, 200],
                            15: [20, 100],
                            17: [20, 20]
                        },
                        // optional, override vector tile parameters:
                        contourLayer: 'contours',
                        elevationKey: 'ele',
                        levelKey: 'level',
                        extent: 4096,
                        buffer: 1
                    })
                ],
                maxzoom: 17
            });

        this._model.map.addLayer({
            id: 'contour_lines',
            type: 'line',
            source: 'contours',
            'source-layer': 'contours',
            minzoom: 12,
            paint: {
                'line-opacity': 0.8,
                'line-color': {
                    'stops': [
                        [13, 'rgba(114, 75, 21, 0.55)'],
                        [16, 'rgba(114, 75, 21, 0.9)']
                    ]
                },
                // level = highest index in thresholds array the elevation is a multiple of
                'line-width': ['match', ['get', 'level'], 1, .6, 0.3]
            }
        }, 'landuse_pitch');

        this._model.map.addLayer({
            id: 'contour_labels',
            type: 'symbol',
            source: 'contours',
            'source-layer': 'contours',
            minzoom: 14,
            filter: ['>', ['get', 'level'], 0],
            layout: {
                'symbol-placement': 'line',
                'text-size': 10,
                'text-field': ['concat', ['number-format', ['get', 'ele'], {}], this._model.distanceUnits === 'mi' ? 'ft' : 'm'],
                'text-font': ['Roboto Regular']
            },
            paint: {
                'text-color': 'rgba(114, 75, 21, 1)',
                'text-halo-color': 'rgba(255, 255, 255, 0.7)',
                'text-halo-width': 2,
                'text-halo-blur': 0.5
            }
        }, 'contour_lines');
    };

    _toggle3DTerrain = (is3D) => {
        if (!this._model.map) {
            return;
        }

        if (!this._model.map.isStyleLoaded()) {
            this._model.map.once('styledata', () => this._toggle3DTerrain(is3D));
            return;
        }

        if (this._model.mapStyleName === 'mapbox_satellite') {
            if (is3D) {
                if (!this._model.map.getSource('mapbox-dem')) {
                    this._model.map.addSource('mapbox-dem', {
                        'type': 'raster-dem',
                        'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
                        'tileSize': 512,
                        'maxzoom': 14
                    });
                }

                this._model.map.setTerrain({
                    'source': 'mapbox-dem',
                    'exaggeration': 1
                });

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 80,
                    duration: 1000
                });
            } else {
                this._model.map.setTerrain();

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 0,
                    duration: 1000
                });
            }
        } else if (this._model.map.getSource('hillshade')) {
            if (is3D) {
                this._model.map.setTerrain({
                    'source': 'hillshade',
                    'exaggeration': 1
                });

                // Adjust camera to see in 3D
                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 80,
                    // bearing: 90,
                    duration: 1000,
                    // zoom: this._model.map.getZoom() - 1
                });
            } else {
                this._model.map.setTerrain();

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 0,
                    duration: 1000
                });
            }
        } else {
            Notification.error(I18n.t('js.maps.3d.not_available'));
        }
    };

    // Initial bearing (deg) from point a to point b, both [lng, lat].
    // Ordered [lng, lat] of the route, from the planner (linePoints) or the ride-show page
    // (per-track rideFeatures[].linePoints).
    _flyoverCoordinates = () => {
        if (this._mapModel.linePoints && this._mapModel.linePoints.length >= 2) {
            return this._mapModel.linePoints.map((point) => [point.latLng.lng, point.latLng.lat]);
        }

        if (Array.isArray(this._mapModel.rideFeatures)) {
            const coords = [];
            this._mapModel.rideFeatures.forEach((feature) => {
                (feature.linePoints || []).forEach((point) => coords.push([point.latLng.lng, point.latLng.lat]));
            });

            if (coords.length >= 2) {
                return coords;
            }
        }

        return [];
    };

    hasFlyoverRoute = () => this._flyoverCoordinates().length >= 2;

    // The camera run itself lives in modules/flyover.js: ~190 lines that all nine
    // map pages used to download before drawing anything, for a feature almost
    // nobody triggers. Loaded on the first call instead.
    //
    // The caller does not await this (RideMap does `map.flyoverRoute()`), which is
    // fine: the run is asynchronous by nature.
    flyoverRoute = (options = {}) => {
        if (!this._model.map) {
            return;
        }

        const coords = this._flyoverCoordinates();
        if (coords.length < 2) {
            return;
        }

        this.stopFlyover();

        return import(/* webpackChunkName: "maps-flyover" */ '@js/modules/maps/modules/flyover')
            .then((flyover) => {
                this._flyoverModule = flyover;

                // The map may have gone (page left, style swapped) while the chunk
                // was in flight.
                if (!this._model.map) {
                    return;
                }

                flyover.start(this, coords, options);
            })
            .catch(() => Notification.info(I18n.t('js.maps.flyover.needs_terrain_map')));
    };

    // Stays SYNCHRONOUS: MapFlyoverButton calls `stopFlyover?.()` and expects the
    // camera to stop now. Nothing can be running before the chunk has loaded, so
    // no module means nothing to stop.
    stopFlyover = () => {
        if (!this._flyoverModule) {
            return;
        }

        this._flyoverModule.stop(this);
    };

    _updateUnitOnMap = () => {
        if (!this._model.map) {
            return;
        }

        // Change peak unit according to current map unit
        this._model.map.setLayoutProperty('poi_mountain_peak', 'text-field', [
            'concat',
            [
                'coalesce',
                ['get', 'name:latin'],
                ['get', 'name']
            ],
            ' ',
            ['get', this._model.distanceUnits === 'mi' ? 'ele_ft' : 'ele'],
            this._model.distanceUnits === 'mi' ? 'ft' : 'm'
        ], STYLE_SETTER_OPTIONS);
    };

    _convertPopupCoordinates = (event) => {
        const coordinates = event.features[0].geometry.coordinates.slice();

        // Ensure that if the map is zoomed out such that multiple copies of the feature are visible, the popup appears over the copy being pointed to.
        while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        return coordinates;
    };

    _manageCluster = (sourceName, pointEvent) => {
        const features = this._model.map.queryRenderedFeatures(pointEvent, {
            layers: [sourceName]
        });
        if (!features[0]) {
            return;
        }

        const clusterId = features[0].properties.cluster_id;

        this._model.map.getSource(sourceName)
            .getClusterExpansionZoom(clusterId)
            .then((zoom) => this.flyTo(features[0].geometry.coordinates, zoom + 1, 800));
    };

    _buildBounds = (bounds) => {
        if (bounds) {
            if (Array.isArray(bounds)) {
                return new LngLatBounds(
                    [parseFloat(bounds[0]), parseFloat(bounds[1])],
                    [parseFloat(bounds[2]), parseFloat(bounds[3])]
                );
            } else if (bounds._sw && bounds._ne) {
                return new LngLatBounds(bounds._sw, bounds._ne);
            } else {
                return new LngLatBounds();
            }
        } else {
            return new LngLatBounds();
        }
    };

    _formattedBounds = () => {
        return [[this._model.mapBounds._sw.lat, this._model.mapBounds._sw.lng], [this._model.mapBounds._ne.lat, this._model.mapBounds._ne.lng]];
    };
}
